import {Injectable} from '@angular/core';
import {
  API_GET_SICK_NOTE,
  API_SEND_SICK_NOTE_FILLED,
  API_SEND_SICK_NOTE_FILLED_V6,
  API_SEND_SICK_NOTE_PENDING,
  API_SEND_SICK_NOTE_PENDING_V6,
  ApiService,
} from './api.service';
import {catchError, map, mergeMap, take, tap} from 'rxjs/operators';
import {EMPTY, Observable, throwError} from 'rxjs';
import {IExtendedSickNote, ProcessingPayload, SickNoteBody} from '../models/sick-note';
import {isSessionExpired} from '../models/session-expired-error';
import {SimpleDialogComponent} from '../components/simple-dialog/simple-dialog.component';
import {NGXLogger} from 'ngx-logger';
import {MatDialog} from '@angular/material/dialog';
import {differenceInHours, formatISO, setHours, setMinutes, setSeconds} from 'date-fns';
import {AuthService} from './auth.service';
import {FeatureFlagsService, isNewDocumentApiEnabled} from './feature-flags.service';

export type SickNoteKind = 'pending' | 'filled';
type FlagValue = boolean | string | null;

/**
 * Pure helper exported for unit testing. Picks the URL to submit a sick note to
 * based on the newDocumentApi feature flag value and the company id read from
 * the Firebase JWT custom claims. Falls back to v4 when the flag is off, when
 * company_id is missing, or when the flag is a non-empty allowlist string that
 * does not include the current company id.
 */
export const resolveSickNoteEndpoint = (
  kind: SickNoteKind,
  flagValue: FlagValue,
  companyId: string | null,
): string => {
  const v4 = kind === 'pending' ? API_SEND_SICK_NOTE_PENDING : API_SEND_SICK_NOTE_FILLED;

  if (!isNewDocumentApiEnabled(flagValue, companyId)) {
    return v4;
  }

  return kind === 'pending' ? API_SEND_SICK_NOTE_PENDING_V6 : API_SEND_SICK_NOTE_FILLED_V6;
};

@Injectable({
  providedIn: 'root'
})
export class SickNoteService {
  public shortId: string;

  private files: File[] = [];
  private data: SickNoteBody;

  constructor(
    private api: ApiService,
    private logger: NGXLogger,
    private dialog: MatDialog,
    private authService: AuthService,
    private featureFlagsService: FeatureFlagsService,
  ) {}

  public setFile(file: File): void {
    this.files.push(file);
  }

  public removeFile(): void {
    this.files.pop();
  }

  public fileExists(): boolean {
    return !!this.files.length;
  }

  /** Drops a submission that was never posted, e.g. one cut short by an expired session. */
  public reset(): void {
    this.files = [];
    this.data = null;
  }

  setData(data: SickNoteBody): void {
    if (!this.data) {
      this.data = {};
    }
    Object.assign(this.data, data);
    this.adjustDates();
  }

  private adjustDates(): void {
    if (!!this.data.appointment_date && this.data.expiration_date) {
      let appointment = new Date(this.data.appointment_date);
      let expiration = new Date(this.data.expiration_date);
      const hours = differenceInHours(expiration, appointment);
      if (hours >= 24) {
        appointment = setHours(appointment, 0);
        appointment = setMinutes(appointment, 0);
        appointment = setSeconds(appointment, 0);
        expiration = setHours(expiration, 0);
        expiration = setMinutes(expiration, 0);
        expiration = setSeconds(expiration, 0);
      }
      this.data.appointment_date = formatISO(appointment);
      this.data.expiration_date = formatISO(expiration);
    }
  }

  /**
   * Send file and data to the right intake endpoint.
   *
   * Reads the newDocumentApi Firebase Remote Config flag and the
   * company_id custom claim from the Firebase JWT, then hands them to
   * resolveSickNoteEndpoint to pick between v4 (legacy) and v6 (new).
   * The actual HTTP submission and error dialog live in postToEndpoint
   * so that resolution and transport are not entangled. Retry-on-error
   * re-enters sendSickNote so a retry picks up any Remote Config or
   * JWT change between attempts.
   */
  sendSickNote(kind: SickNoteKind): Observable<string> {
    return this.authService.userClaims.pipe(
      take(1),
      mergeMap(([, claims]) => {
        const companyId = (claims?.company_id as string | undefined) ?? null;
        const flagValue = this.featureFlagsService.featureFlags.newDocumentApi;
        const endpoint = resolveSickNoteEndpoint(kind, flagValue, companyId);
        return this.postToEndpoint(endpoint, kind);
      }),
    );
  }

  private postToEndpoint(endpoint: string, kind: SickNoteKind): Observable<string> {
    const formData: FormData = new FormData();
    this.buildFormData(formData, this.data);
    this.files.forEach((file: File) => formData.append('file', file, file?.name));

    return this.api.http.post<{short_id: string}>(endpoint, formData).pipe(
      map(response => {
        this.files = [];
        this.data = null;
        return response?.short_id;
      }),
      catchError((error) => {
        this.logger.debug(error);
        if (isSessionExpired(error)) {
          return throwError(error);
        }
        const duplicated = error?.status === 409;
        const errorMsg = duplicated
          ? 'Já existe um atestado igual a esse cadastrado. Você pode pedir ajuda no botão acima se precisar corrigi-lo'
          : 'Ocorreu um erro ao enviar seu atestado. Se o problema continuar, fale com o suporte pelo botão de Ajuda acima';
        const data = {
          okButton: duplicated ? 'OK' : 'Tentar novamente',
          noButton: 'Cancelar',
          title: duplicated ? 'Atestado duplicado' : 'Erro ao enviar',
          content: errorMsg
        };
        return SimpleDialogComponent.open(this.dialog, data).afterClosed()
          .pipe(
            mergeMap((tryAgain) => {
              if (!duplicated && tryAgain) {
                // Re-resolve through sendSickNote so a retry picks up any
                // Remote Config or JWT change since the previous attempt.
                return this.sendSickNote(kind);
              } else {
                return EMPTY;
              }
            })
          );
      }),
      tap((response) => {
        this.logger.debug('sendSickNote response', response);
      }),
    );
  }

  buildFormData(formData: FormData, data: any, parentKey?: string): void {
    if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File) && !(Array.isArray(data) && !data.length)) {
      Object.keys(data).forEach(key => {
        this.buildFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key);
      });
    } else {
      const value = data == null ? '' : data;
      formData.append(parentKey, value);
    }
  }

  blobToFile = (theBlob: Blob, fileName: string, lastModified?: number): File => {
    const b: any = theBlob;
    // A Blob() is almost a File() - it's just missing the two properties below
    b.lastModified = lastModified || (new Date().getTime() / 1000);
    b.name = fileName;

    // Cast to a File() type
    return theBlob as File;
  }

  getSickNote(shortId: string): Observable<IExtendedSickNote> {
    return this.api.http.get<IExtendedSickNote>(API_GET_SICK_NOTE(shortId));
  }

  getSickNoteProcessingStatus(shortId: string): Observable<ProcessingPayload> {
    return this.api.http.get<ProcessingPayload>(`${API_GET_SICK_NOTE(shortId)}/process`);
  }
}
